import React from 'react';
import PropTypes from 'prop-types';
import ObjectHelper from 'foundation/object-helper';
import BackLink from './back-link';
import ExtraPanel from './extra-panel';
import SelectPanel from './select-panel';
import {
    PRODUCT_TYPE_PARKING,
    PRODUCT_TYPE_EXTRAS,
    PRODUCT_TAG_TICKET_PLUS_EXTRA,
    MAX_HOSPITALITY_TICKET_PLUS_EXTRA
} from '../../../general/js/basket/constants';
import {
    hasValue
} from '../../../general/js/general-helpers';
import ValidationHelper from '../../../general/js/basket/validation-helper';
import { ROUTE_TICKETS, SCENARIO_STANDARD_WITH_EXTRAS } from './constants';

export default class SelectExtrasForm extends React.Component {
    constructor(props) {
        super(props);
        this.onSelectExtraProduct = this.onSelectExtraProduct.bind(this);
    }

    onSelectExtraProduct(product, productGroup, isChecked) {
        if (!isChecked) {
            // remove from basket
            this.props.onUpdateProductQuantity(product, productGroup, 0);
        } else {
            let quantity = 1;

            // finding main product of selected product group
            const mainProduct = productGroup.products.find((p) => p.type === this.props.currentProductType);

            // trying to find it in basket
            if (productGroup.id in this.props.basket.productGroups) {
                const mainProductInBasket = this.props.basket.productGroups[productGroup.id].products.find((basketProduct) => basketProduct.id === mainProduct.id);
                if (mainProductInBasket) {
                    quantity = this.setExtraProductQuantity(product, mainProductInBasket, quantity);
                }
            }

            this.props.onUpdateProductQuantity(product, productGroup, quantity);
        }
    }

    setExtraProductQuantity = (product = null, mainProductInBasket = null, quantity = 0) => {
        if (!hasValue(product) || !hasValue(mainProductInBasket)) {
            return 0;
        }

        switch (product.tag) {
            case PRODUCT_TAG_TICKET_PLUS_EXTRA:
                quantity = mainProductInBasket.quantity > MAX_HOSPITALITY_TICKET_PLUS_EXTRA ? MAX_HOSPITALITY_TICKET_PLUS_EXTRA : mainProductInBasket.quantity;
                break;
            default:
                quantity = mainProductInBasket.quantity;
        }

        return quantity;
    }

    getGroupedExtrasList() {
        const extrasById = this.props.productGroupsList.reduce((result, productGroup) => {
            const extras = productGroup.products.filter((product) => product.type === PRODUCT_TYPE_EXTRAS);
            extras.forEach((extrasProduct) => {
                if (!(extrasProduct.tag in result)) {
                    result[extrasProduct.tag] = {};
                }

                if (!(extrasProduct.id in result[extrasProduct.tag])) {
                    result[extrasProduct.tag][extrasProduct.id] = {
                        product: { ...extrasProduct },
                        productGroup
                    };
                }
            });

            return result;
        }, {});

        const extrasList = Object.keys(extrasById).map((key) => {
            const extraGroup = extrasById[key];
            const items = Object.keys(extraGroup).map((productKey) => extraGroup[productKey]);
            const title = items[0].product.tag === PRODUCT_TAG_TICKET_PLUS_EXTRA ? '' : items[0].product.title;

            return {
                id: key,
                title: title,
                items
            };
        });

        const { grandstandText } = this.getDictionaryFromProps();

        if (extrasList.length > 0) {
            extrasList[0].description = this.parseDictionaryItem(grandstandText);
        }

        return extrasList;
    }

    getParkingList() {
        const productGroupsList = this.props.productGroupsList;

        if (!productGroupsList.length || !productGroupsList[0].products) return [];

        const parkingProductsList = ObjectHelper.clone(productGroupsList[0].products.filter((product) =>
            product.type === PRODUCT_TYPE_PARKING));

        // HAS BEEN CHANGED
        // find product groups that are selected for current way
        const currentProductGroupsIds = productGroupsList.map((productGroup) => productGroup.id);
        const currentBasketProductGroups = Object.values(this.props.basket.productGroups).filter((basketProductGroup) => currentProductGroupsIds.indexOf(basketProductGroup.id) !== -1);

        parkingProductsList.forEach((parking) => {
            // fill the information about quantity based on currentBasketProductGroups
            currentBasketProductGroups.forEach((basketProductGroup) => {
                basketProductGroup.products.forEach((basketProduct) => {
                    if (basketProduct.type === parking.type &&
                        basketProduct.tag === parking.tag
                    ) {
                        parking.quantity = basketProduct.quantity;
                    }
                });
            });
        });

        return ValidationHelper.getPreparedProductsList(parkingProductsList);
    }

    getTicketsList() {
        const productGroupsList = this.props.productGroupsList;

        if (!productGroupsList.length || !productGroupsList[0].products) return [];

        const ticketProductsList = ObjectHelper.clone(productGroupsList[0].products.filter((product) =>
            product.type === PRODUCT_TYPE_EXTRAS));

        // HAS BEEN CHANGED
        // find product groups that are selected for current way
        const currentProductGroupsIds = productGroupsList.map((productGroup) => productGroup.id);
        const currentBasketProductGroups = Object.values(this.props.basket.productGroups).filter((basketProductGroup) => currentProductGroupsIds.indexOf(basketProductGroup.id) !== -1);

        ticketProductsList.forEach((ticket) => {
            // fill the information about quantity based on currentBasketProductGroups
            currentBasketProductGroups.forEach((basketProductGroup) => {
                basketProductGroup.products.forEach((basketProduct) => {
                    if (basketProduct.type === ticket.type &&
                        basketProduct.tag === ticket.tag
                    ) {
                        ticket.quantity = basketProduct.quantity;
                    }
                });
            });
        });

        return ValidationHelper.getPreparedProductsList(ticketProductsList);
    }

    renderExtrasPanels() {
        const extraGroupsList = this.getGroupedExtrasList();

        return extraGroupsList.map((extraGroup) => (
            <ExtraPanel
                basket={this.props.basket}
                key={extraGroup.id}
                extraGroup={extraGroup}
                onSelectExtraProduct={this.onSelectExtraProduct}
            />
        ));
    }

    renderCarsPanels() {
        const parkingList = this.getParkingList();

        return parkingList.map((parking) => (
            <SelectPanel
                dictionary={this.props.dictionary}
                key={parking.id}
                product={parking}
                productGroupsList={this.props.productGroupsList}
                onApplyProductsQuantity={this.props.onApplyProductsQuantity}
                rootCssClass='tb-panels-list__item'
            />
        ));
    }

    renderTicketsPanel() {
        const ticketsList = this.getTicketsList();

        return ticketsList.map((ticket) => (
            <SelectPanel
                dictionary={this.props.dictionary}
                key={ticket.id}
                product={ticket}
                productGroupsList={this.props.productGroupsList}
                onApplyProductsQuantity={this.props.onApplyProductsQuantity}
                rootCssClass='tb-panels-list__item'
            />
        ));
    }

    parseDictionaryItem(item = '') {
        if (!hasValue(item)) {
            return item;
        }

        if (item.includes('TicketBuilder_')) {
            return '';
        }

        return item;
    }

    getDictionaryFromProps() {
        const { dictionary } = this.props;

        if (!hasValue(dictionary)) {
            return {
                addExtrasSubtitle: '',
                addExtrasTitle: '',
                extrasParkingText: '',
                grandstandText: '',
                privateTableTitle: '',
                privateTableText: '',
                privateTableLinkText: '',
                privateTableLinkUrl: '',
            };
        }

        return dictionary;
    }

    render() {
        const { addExtrasSubtitle, addExtrasTitle } = this.getDictionaryFromProps();

        return (
            <div className="tb-form">
                <div className="panel__heading">
                    <h3 className="title">{this.parseDictionaryItem(addExtrasSubtitle)}</h3>
                    <div className="extra-panel__descr copy">
                        <span>{this.parseDictionaryItem(addExtrasTitle)}</span>
                    </div>

                </div>
                <div className="tb-form__list">
                    {
                        this.props.currentScenario === SCENARIO_STANDARD_WITH_EXTRAS ?
                            this.renderTicketsPanel() :
                            this.renderExtrasPanels()
                    }
                </div>
                <div className="tb-form__footer tb-form__footer--extras">
                    <div className="tb-form__change-step">
                        <BackLink route={ROUTE_TICKETS}>Back</BackLink>
                    </div>
                </div>
            </div>
        );
    }
}

SelectExtrasForm.propTypes = {
    dictionary: PropTypes.object.isRequired,
    basket: PropTypes.object,
    productGroupsList: PropTypes.array,
    productsList: PropTypes.array,
    currentProductType: PropTypes.string.isRequired,
    onApplyProductsQuantity: PropTypes.func,
    onUpdateProductQuantity: PropTypes.func,
    currentScenario: PropTypes.string.isRequired,
};
